// Brutal portfolio page — monolithic iframe block
// Neo-brutalism design bureau with brand sets (acid, pop, noir, candy). Fixed to "acid" set.

export type BrutalPortfolioBrand = 'acid' | 'pop' | 'noir' | 'candy'
export interface BrutalPortfolioPageData { brand?: BrutalPortfolioBrand }
export const DEFAULT_BRUTAL_PORTFOLIO_DATA: BrutalPortfolioPageData = { brand: 'acid' }

// ── CSS ───────────────────────────────────────────────────────────────────────
const BRUTAL_PORTFOLIO_CSS = `
/* ============ БРЕНД-СЕТЫ (ТЕМЫ) ============ */
:root{
  --bw:3px;
  --font-d:'Unbounded',system-ui,sans-serif;
  --font-m:'IBM Plex Mono',ui-monospace,monospace;
}
[data-brand="acid"]{
  --bg:#DFF247; --surface:#FFFFFF; --ink:#141414;
  --accent:#7C4DFF; --accent2:#FF6EC7; --on-accent:#FFFFFF;
  --dot:rgba(20,20,20,.16);
}
[data-brand="pop"]{
  --bg:#FF6FB2; --surface:#FFF4F9; --ink:#1A1023;
  --accent:#FFD21F; --accent2:#3FA7FF; --on-accent:#141414;
  --dot:rgba(26,16,35,.16);
}
[data-brand="noir"]{
  --bg:#F1EEE7; --surface:#FFFFFF; --ink:#121212;
  --accent:#FF4B1F; --accent2:#2244FF; --on-accent:#FFFFFF;
  --dot:rgba(18,18,18,.14);
}
[data-brand="candy"]{
  --bg:#CDBBFF; --surface:#FFF7EA; --ink:#251743;
  --accent:#FF8A2A; --accent2:#2FD98B; --on-accent:#141414;
  --dot:rgba(37,23,67,.15);
}

/* ============ БАЗА ============ */
*{margin:0;padding:0;box-sizing:border-box}
html{scroll-behavior:smooth}
body{
  background:var(--bg); color:var(--ink);
  font-family:var(--font-m); font-size:16px; line-height:1.6;
  background-image:radial-gradient(var(--dot) 1.6px, transparent 1.6px);
  background-size:28px 28px;
  overflow-x:hidden;
}
body,.project-card,.btn,.marquee,.sw-btn,.acc-item,.step,.stat,.quote-card,.stick,.hero-word,.swatch-color,.mmenu,.pc-arrow,.filter-btn{
  transition:background-color .4s ease,color .4s ease,border-color .4s ease,box-shadow .25s ease,transform .25s ease;
}
::selection{background:var(--ink);color:var(--bg)}
img{display:block;max-width:100%}
a{color:inherit}
h1,h2,h3{font-family:var(--font-d);text-transform:uppercase;line-height:1.05;letter-spacing:-.01em}
button{font-family:inherit;color:inherit;background:none;border:none;cursor:pointer}
:focus-visible{outline:3px solid var(--accent);outline-offset:3px}
.wrap{max-width:1280px;margin:0 auto;padding:0 clamp(16px,4vw,40px)}

.outline{color:transparent;-webkit-text-stroke:2.5px var(--ink)}
@supports not (-webkit-text-stroke:1px black){.outline{color:var(--accent)}}

.noise{position:fixed;inset:0;z-index:2000;pointer-events:none;opacity:.05;
  background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");}

.kicker{font-family:var(--font-m);font-weight:700;font-size:.8rem;letter-spacing:.12em;text-transform:uppercase;margin-bottom:14px}
.sec-head{margin-bottom:clamp(28px,5vw,56px)}
.sec-head h2{font-size:clamp(1.9rem,5.4vw,4rem);font-weight:900}

.btn{display:inline-block;font-family:var(--font-m);font-weight:700;text-transform:uppercase;letter-spacing:.06em;
  font-size:.9rem;padding:14px 24px;border:var(--bw) solid var(--ink);background:var(--surface);color:var(--ink);
  box-shadow:6px 6px 0 var(--ink);text-decoration:none}
.btn:hover{transform:translate(-3px,-3px);box-shadow:9px 9px 0 var(--ink)}
.btn:active{transform:translate(2px,2px);box-shadow:2px 2px 0 var(--ink)}
.btn-accent{background:var(--accent);color:var(--on-accent)}

/* ============ НАВ ============ */
.nav{position:fixed;top:0;left:0;right:0;z-index:100;background:var(--bg);border-bottom:var(--bw) solid var(--ink)}
.nav-inner{max-width:1280px;margin:0 auto;padding:12px clamp(16px,4vw,40px);display:flex;align-items:center;gap:20px}
.logo{font-family:var(--font-d);font-weight:900;font-size:1.5rem;text-decoration:none;display:flex;align-items:center;gap:8px}
.logo svg{width:22px;height:22px;flex:none}
.logo sup{font-size:.6em}
.nav-links{display:flex;gap:22px;margin-left:auto}
.nav-links a{font-weight:700;font-size:.82rem;text-transform:uppercase;letter-spacing:.08em;text-decoration:none;position:relative}
.nav-links a::after{content:"";position:absolute;left:0;bottom:-4px;width:100%;height:3px;background:var(--accent);transform:scaleX(0);transform-origin:left;transition:transform .25s}
.nav-links a:hover::after{transform:scaleX(1)}
.nav-right{display:flex;align-items:center;gap:14px;margin-left:auto}
.nav-links + .nav-right{margin-left:0}
.switcher{display:flex;gap:6px}
.sw-btn{width:32px;height:32px;border:var(--bw) solid var(--ink);font-family:var(--font-m);font-weight:700;font-size:.62rem;display:grid;place-items:center;box-shadow:3px 3px 0 var(--ink)}
.sw-btn:hover{transform:translate(-2px,-2px);box-shadow:5px 5px 0 var(--ink)}
.sw-btn.active{background:var(--ink)!important;color:var(--bg);transform:translate(-2px,-2px);box-shadow:5px 5px 0 var(--accent)}
.sw-btn[data-set="acid"]{background:#DFF247}
.sw-btn[data-set="pop"]{background:#FF6FB2}
.sw-btn[data-set="noir"]{background:#F1EEE7}
.sw-btn[data-set="candy"]{background:#CDBBFF}
.burger{display:none;width:44px;height:44px;border:var(--bw) solid var(--ink);background:var(--surface);box-shadow:4px 4px 0 var(--ink);flex-direction:column;justify-content:center;align-items:center;gap:6px}
.burger span{display:block;width:20px;height:3px;background:var(--ink);transition:transform .3s,opacity .3s}
.burger.open span:nth-child(1){transform:translateY(9px) rotate(45deg)}
.burger.open span:nth-child(2){opacity:0}
.burger.open span:nth-child(3){transform:translateY(-9px) rotate(-45deg)}

.mmenu{position:fixed;inset:0;z-index:90;background:var(--bg);transform:translateY(-102%);transition:transform .45s cubic-bezier(.7,0,.3,1);padding:110px clamp(20px,6vw,60px) 40px;display:flex;flex-direction:column;gap:8px}
.mmenu.open{transform:translateY(0)}
.mmenu a.big{font-family:var(--font-d);font-weight:900;font-size:clamp(1.6rem,7vw,2.6rem);text-transform:uppercase;text-decoration:none;border-bottom:var(--bw) solid var(--ink);padding:10px 0;display:flex;justify-content:space-between;align-items:center}
.mmenu a.big small{font-family:var(--font-m);font-size:.75rem;font-weight:700}
.mmenu a.big:hover{background:var(--accent);color:var(--on-accent);padding-left:12px}
.mmenu .mm-meta{margin-top:auto;font-size:.78rem;text-transform:uppercase;letter-spacing:.08em;display:flex;flex-wrap:wrap;gap:14px;justify-content:space-between}

/* ============ HERO ============ */
.hero{position:relative;padding:calc(76px + clamp(40px,8vh,90px)) 0 clamp(40px,7vh,80px);overflow:hidden}
.hero-watermark{position:absolute;top:6%;right:-4%;font-family:var(--font-d);font-weight:900;font-size:clamp(180px,36vw,540px);line-height:1;color:transparent;-webkit-text-stroke:2px var(--ink);opacity:.07;pointer-events:none;user-select:none}
.hero-grid{display:grid;grid-template-columns:1.25fr .75fr;gap:clamp(24px,4vw,60px);align-items:center;position:relative}
.hero h1{font-size:clamp(2.2rem,7.2vw,5.6rem);font-weight:900;margin:14px 0 22px}
.hero-word{display:inline-block;background:var(--accent);color:var(--on-accent);border:var(--bw) solid var(--ink);box-shadow:7px 7px 0 var(--ink);padding:.02em .18em .06em;transform:rotate(-2deg)}
.hero-sub{max-width:52ch;font-size:clamp(.9rem,1.4vw,1.05rem);margin-bottom:30px}
.hero-sub.swap{animation:subIn .45s ease}
@keyframes subIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
.hero-cta{display:flex;gap:16px;flex-wrap:wrap;margin-bottom:34px}
.hero-meta{display:flex;gap:18px;flex-wrap:wrap;font-size:.75rem;font-weight:700;text-transform:uppercase;letter-spacing:.1em}
.hero-meta .pill{border:var(--bw) solid var(--ink);background:var(--surface);padding:6px 12px;box-shadow:4px 4px 0 var(--ink)}
.hero-right{position:relative;min-height:440px}
.badge{position:absolute;top:6%;right:4%;width:clamp(130px,14vw,190px);animation:spin 14s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.bigstar{position:absolute;left:2%;top:0;width:clamp(70px,8vw,110px);animation:spin 22s linear infinite reverse}
.stick{position:absolute;font-family:var(--font-m);font-weight:700;font-size:.85rem;text-transform:uppercase;border:var(--bw) solid var(--ink);padding:10px 16px;box-shadow:6px 6px 0 var(--ink)}
.stick-a{background:var(--accent2);left:6%;bottom:22%;transform:rotate(-5deg)}
.stick-b{background:var(--surface);right:10%;bottom:4%;transform:rotate(4deg)}
.stick-c{background:var(--accent);color:var(--on-accent);left:24%;top:34%;transform:rotate(2deg)}
.scroll-hint{position:absolute;left:clamp(16px,4vw,40px);bottom:14px;font-size:.72rem;font-weight:700;letter-spacing:.15em;text-transform:uppercase;animation:bnc 1.6s ease-in-out infinite}
@keyframes bnc{0%,100%{transform:translateY(0)}50%{transform:translateY(6px)}}

/* ============ MARQUEE ============ */
.marquee{overflow:hidden;border-block:var(--bw) solid var(--ink);background:var(--accent);color:var(--on-accent);padding:14px 0}
.marquee.alt{background:var(--accent2);color:var(--ink)}
.mq-track{display:flex;width:max-content;animation:mq 24s linear infinite}
.marquee:hover .mq-track{animation-play-state:paused}
.mq-group{display:flex;flex:none}
.mq-group span{font-family:var(--font-d);font-weight:700;text-transform:uppercase;font-size:clamp(.95rem,2vw,1.25rem);letter-spacing:.06em;padding:0 18px;white-space:nowrap}
.mq-group i{font-style:normal;padding:0 4px}
@keyframes mq{to{transform:translateX(-50%)}}

/* ============ РАБОТЫ ============ */
section{padding:clamp(56px,9vh,110px) 0}
.filters{display:flex;gap:10px;flex-wrap:wrap;margin-bottom:34px}
.filter-btn{border:var(--bw) solid var(--ink);background:var(--surface);font-weight:700;font-size:.78rem;text-transform:uppercase;letter-spacing:.08em;padding:9px 16px;box-shadow:4px 4px 0 var(--ink)}
.filter-btn:hover{transform:translate(-2px,-2px);box-shadow:6px 6px 0 var(--ink)}
.filter-btn.active{background:var(--ink);color:var(--bg)}
.works-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:clamp(18px,2.6vw,32px)}
.project-card{border:var(--bw) solid var(--ink);background:var(--surface);box-shadow:8px 8px 0 var(--ink);display:flex;flex-direction:column;text-decoration:none}
.project-card:hover{transform:translate(-4px,-4px);box-shadow:12px 12px 0 var(--ink)}
.project-card.is-hidden{display:none}
.project-card.pop{animation:popIn .4s ease}
@keyframes popIn{from{opacity:0;transform:scale(.92)}to{opacity:1;transform:scale(1)}}
.pc-media{overflow:hidden;border-bottom:var(--bw) solid var(--ink);aspect-ratio:4/3}
.pc-media img{width:100%;height:100%;object-fit:cover;transition:transform .5s ease}
.project-card:hover .pc-media img{transform:scale(1.07) rotate(1deg)}
.pc-body{padding:18px 18px 20px;display:flex;flex-direction:column;gap:8px;flex:1}
.pc-top{display:flex;justify-content:space-between;align-items:center;font-size:.72rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase}
.pc-idx{background:var(--accent2);border:2px solid var(--ink);padding:2px 8px}
.pc-body h3{font-size:clamp(1.05rem,1.8vw,1.4rem);font-weight:700}
.pc-tags{font-size:.75rem;opacity:.75;text-transform:uppercase;letter-spacing:.06em}
.pc-arrow{margin-top:auto;align-self:flex-end;width:42px;height:42px;border:var(--bw) solid var(--ink);background:var(--accent);color:var(--on-accent);display:grid;place-items:center;font-size:1.1rem;font-weight:700;box-shadow:4px 4px 0 var(--ink)}
.project-card:hover .pc-arrow{transform:rotate(45deg)}

/* ============ СТУДИЯ ============ */
.studio-grid{display:grid;grid-template-columns:1.1fr .9fr;gap:clamp(28px,4vw,64px);align-items:start}
.manifesto p{font-size:clamp(.95rem,1.5vw,1.1rem);margin-bottom:16px}
.manifesto strong{background:var(--accent2);padding:0 6px;border:2px solid var(--ink)}
.stats{display:grid;grid-template-columns:1fr 1fr;gap:18px}
.stat{border:var(--bw) solid var(--ink);background:var(--surface);box-shadow:6px 6px 0 var(--ink);padding:20px}
.stat-num{font-family:var(--font-d);font-weight:900;font-size:clamp(1.7rem,3.4vw,2.7rem);display:block;line-height:1}
.stat-num em{font-style:normal;color:var(--accent)}
.stat small{font-size:.72rem;font-weight:700;text-transform:uppercase;letter-spacing:.1em}
.quotes{display:grid;grid-template-columns:repeat(3,1fr);gap:clamp(18px,2.6vw,30px);margin-top:clamp(36px,6vh,64px)}
.quote-card{border:var(--bw) solid var(--ink);background:var(--surface);box-shadow:8px 8px 0 var(--ink);padding:26px 20px 20px;position:relative}
.quote-card::before{content:"";position:absolute;top:-14px;left:50%;transform:translateX(-50%) rotate(-3deg);width:90px;height:22px;background:var(--accent2);border:2px solid var(--ink)}
.quote-card:nth-child(1){transform:rotate(-1.6deg)}
.quote-card:nth-child(2){transform:rotate(1.2deg)}
.quote-card:nth-child(3){transform:rotate(-1deg)}
.quote-card:hover{transform:rotate(0) translateY(-4px)}
.quote-card p{font-size:.9rem;margin-bottom:16px}
.quote-card footer{font-size:.72rem;font-weight:700;text-transform:uppercase;letter-spacing:.08em;opacity:.8}

/* ============ ПРОЦЕСС ============ */
.process-grid{display:grid;grid-template-columns:.85fr 1.15fr;gap:clamp(28px,4vw,70px)}
.proc-sticky{position:sticky;top:110px;align-self:start}
.step-counter{display:inline-grid;place-items:center;min-width:110px;min-height:110px;background:var(--accent);color:var(--on-accent);border:var(--bw) solid var(--ink);box-shadow:8px 8px 0 var(--ink);font-family:var(--font-d);font-weight:900;font-size:2.6rem;margin-top:22px}
.proc-note{margin-top:20px;font-size:.85rem;max-width:34ch}
.step{border:var(--bw) solid var(--ink);background:var(--surface);box-shadow:7px 7px 0 var(--ink);padding:24px;margin-bottom:22px;opacity:.45;transition:opacity .4s,transform .4s}
.step.active{opacity:1;transform:translate(-3px,-3px)}
.step h3{font-size:1.15rem;margin-bottom:10px;display:flex;gap:12px;align-items:baseline}
.step h3 b{font-family:var(--font-m);font-size:.75rem;background:var(--accent2);border:2px solid var(--ink);padding:2px 8px}
.step p{font-size:.9rem}

/* ============ УСЛУГИ ============ */
.acc-item{border:var(--bw) solid var(--ink);background:var(--surface);box-shadow:6px 6px 0 var(--ink);margin-bottom:16px}
.acc-head{width:100%;display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:16px;padding:20px 22px;text-align:left}
.acc-head .n{font-weight:700;font-size:.8rem}
.acc-head h3{font-size:clamp(1rem,2.4vw,1.5rem)}
.acc-x{width:38px;height:38px;border:var(--bw) solid var(--ink);background:var(--accent);color:var(--on-accent);display:grid;place-items:center;font-size:1.2rem;font-weight:700;transition:transform .35s}
.acc-item.open .acc-x{transform:rotate(45deg)}
.acc-body{max-height:0;overflow:hidden;transition:max-height .45s ease}
.acc-inner{padding:0 22px 24px;display:flex;flex-direction:column;gap:14px}
.acc-inner p{font-size:.92rem;max-width:70ch}
.chips{display:flex;gap:8px;flex-wrap:wrap}
.chips span{border:2px solid var(--ink);background:var(--bg);font-size:.7rem;font-weight:700;text-transform:uppercase;letter-spacing:.06em;padding:5px 10px}
.price{font-family:var(--font-d);font-weight:700;font-size:1rem;background:var(--accent2);border:var(--bw) solid var(--ink);box-shadow:4px 4px 0 var(--ink);padding:8px 14px;align-self:flex-start}

/* ============ ПАСПОРТ БРЕНДА ============ */
.passport{border-block:var(--bw) solid var(--ink);background:var(--surface)}
.pp-grid{display:grid;grid-template-columns:1fr 1fr;gap:clamp(28px,4vw,70px);align-items:start}
.pp-name{font-size:clamp(1.6rem,4vw,2.8rem);font-weight:900;margin-bottom:12px}
.pp-name mark{background:var(--accent);color:var(--on-accent);padding:0 .2em;border:var(--bw) solid var(--ink);box-shadow:5px 5px 0 var(--ink);display:inline-block;transform:rotate(-1.5deg)}
.pp-tag{font-weight:700;text-transform:uppercase;letter-spacing:.08em;font-size:.85rem;margin-bottom:14px}
.pp-sub{font-size:.92rem;margin-bottom:18px}
.pp-tone{display:flex;gap:8px;flex-wrap:wrap}
.pp-tone span{border:2px solid var(--ink);background:var(--bg);padding:6px 12px;font-size:.72rem;font-weight:700;text-transform:uppercase;letter-spacing:.08em;box-shadow:3px 3px 0 var(--ink)}
.swatches{display:grid;grid-template-columns:repeat(5,1fr);gap:12px;margin-bottom:22px}
.swatch{border:var(--bw) solid var(--ink);box-shadow:5px 5px 0 var(--ink);background:var(--bg)}
.swatch-color{height:86px;border-bottom:var(--bw) solid var(--ink)}
.swatch small{display:block;padding:8px 6px;font-size:.6rem;font-weight:700;text-transform:uppercase;letter-spacing:.04em;line-height:1.5}
.swatch small b{display:block;font-size:.62rem}
.pp-fonts{font-size:.78rem;text-transform:uppercase;letter-spacing:.08em;border:2px dashed var(--ink);padding:12px 14px}

/* ============ ФУТЕР ============ */
.footer{padding:clamp(60px,10vh,120px) 0 0;overflow:hidden}
.footer h2{font-size:clamp(2rem,6.4vw,5rem);font-weight:900;margin-bottom:30px}
.foot-mail{font-size:clamp(1rem,2.6vw,1.6rem)}
.foot-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px;margin:clamp(36px,6vh,60px) 0;padding-top:28px;border-top:var(--bw) solid var(--ink)}
.foot-grid h4{font-family:var(--font-m);font-size:.72rem;text-transform:uppercase;letter-spacing:.12em;margin-bottom:12px;opacity:.7}
.foot-grid a{display:block;text-decoration:none;font-weight:700;font-size:.85rem;text-transform:uppercase;padding:4px 0}
.foot-grid a:hover{background:var(--accent);color:var(--on-accent);display:inline-block;padding:4px 8px}
.foot-bottom{display:flex;justify-content:space-between;gap:14px;flex-wrap:wrap;font-size:.72rem;text-transform:uppercase;letter-spacing:.1em;padding-bottom:20px}
.foot-giant{font-family:var(--font-d);font-weight:900;font-size:clamp(90px,22vw,340px);line-height:.85;text-align:center;color:transparent;-webkit-text-stroke:3px var(--ink);user-select:none;transform:translateY(8%)}
@supports not (-webkit-text-stroke:1px black){.foot-giant{color:var(--ink);opacity:.15}}

/* ============ REVEAL ============ */
[data-reveal]{opacity:0;transform:translateY(26px);transition:opacity .7s ease,transform .7s ease}
[data-reveal].in{opacity:1;transform:none}

/* ============ АДАПТИВ ============ */
@media (max-width:1020px){
  .nav-links{display:none}
  .burger{display:flex}
  .hero-grid{grid-template-columns:1fr}
  .hero-right{min-height:300px}
  .works-grid{grid-template-columns:1fr 1fr}
  .studio-grid,.process-grid,.pp-grid{grid-template-columns:1fr}
  .proc-sticky{position:static}
  .quotes{grid-template-columns:1fr 1fr}
}
@media (max-width:640px){
  .works-grid,.quotes,.stats{grid-template-columns:1fr}
  .swatches{grid-template-columns:repeat(3,1fr)}
  .swatch-color{height:64px}
  .hero-right{min-height:240px}
  .stick{font-size:.7rem;padding:8px 12px}
  .foot-grid{grid-template-columns:1fr}
  .sw-btn{width:28px;height:28px}
  .nav-inner{gap:12px}
  .logo{font-size:1.25rem}
}
@media (prefers-reduced-motion:reduce){
  html{scroll-behavior:auto}
  *,*::before,*::after{animation-duration:.01ms!important;animation-iteration-count:1!important;transition-duration:.01ms!important}
  [data-reveal]{opacity:1;transform:none}
}
`

// ── HTML body ─────────────────────────────────────────────────────────────────
const BRUTAL_PORTFOLIO_HTML_BODY = `
<div class="noise" aria-hidden="true"></div>

<!-- НАВИГАЦИЯ -->
<header class="nav">
  <div class="nav-inner">
    <a class="logo" href="#top" aria-label="BST — на главную">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" fill="var(--accent)" stroke="var(--ink)" stroke-width="1.6"/></svg>
      BST<sup>®</sup>
    </a>
    <nav class="nav-links" aria-label="Основная навигация">
      <a href="#works">Работы</a>
      <a href="#studio">Студия</a>
      <a href="#process">Процесс</a>
      <a href="#services">Услуги</a>
      <a href="#contact">Контакт</a>
    </nav>
    <div class="nav-right">
      <div class="switcher" role="group" aria-label="Выбор бренд-сета">
        <button type="button" class="sw-btn active" data-set="acid"  title="Сет 01 — ACID">01</button>
        <button type="button" class="sw-btn" data-set="pop"   title="Сет 02 — POP">02</button>
        <button type="button" class="sw-btn" data-set="noir"  title="Сет 03 — NOIR">03</button>
        <button type="button" class="sw-btn" data-set="candy" title="Сет 04 — CANDY">04</button>
      </div>
      <button type="button" class="burger" id="burger" aria-label="Меню" aria-expanded="false">
        <span></span><span></span><span></span>
      </button>
    </div>
  </div>
</header>

<!-- МОБИЛЬНОЕ МЕНЮ -->
<div class="mmenu" id="mmenu" aria-hidden="true">
  <a class="big" href="#works">Работы <small>01</small></a>
  <a class="big" href="#studio">Студия <small>02</small></a>
  <a class="big" href="#process">Процесс <small>03</small></a>
  <a class="big" href="#services">Услуги <small>04</small></a>
  <a class="big" href="#contact">Контакт <small>05</small></a>
  <div class="mm-meta">
    <span>hello@bst.studio</span>
    <span>сет: <span id="mmBrand">01 / ACID</span></span>
  </div>
</div>

<main id="top">

<!-- HERO -->
<section class="hero">
  <div class="hero-watermark" aria-hidden="true">BST</div>
  <div class="wrap hero-grid">
    <div>
      <p class="kicker">( независимое дизайн-бюро · est. 2016 )</p>
      <h1>Дизайн,<br>который<br><span class="hero-word" id="heroWord">КРИЧИТ</span></h1>
      <p class="hero-sub" id="heroSub">Нео-брутализм без правил: жирные контуры, кислотные пятна и типографика, которую видно из космоса.</p>
      <div class="hero-cta">
        <a class="btn btn-accent" href="#works">Смотреть работы ✦</a>
        <a class="btn" href="#contact">Обсудить проект</a>
      </div>
      <div class="hero-meta">
        <span class="pill">Москва — Белград — Тбилиси</span>
        <span class="pill">сейчас: сет <span id="heroBrand">01 / ACID</span></span>
      </div>
    </div>
    <div class="hero-right" aria-hidden="true">
      <svg class="bigstar" viewBox="0 0 24 24"><path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" fill="var(--accent2)" stroke="var(--ink)" stroke-width="1.4"/></svg>
      <svg class="badge" viewBox="0 0 200 200">
        <defs><path id="cir" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0"/></defs>
        <circle cx="100" cy="100" r="98" fill="var(--surface)" stroke="var(--ink)" stroke-width="3"/>
        <text style="font-family:var(--font-m);font-weight:700;font-size:17px;letter-spacing:3px" fill="var(--ink)">
          <textPath href="#cir">BST • ГРОМКИЙ ДИЗАЙН • BST • ГРОМКИЙ ДИЗАЙН •</textPath>
        </text>
        <path d="M100 55l9 32 32 9-32 9-9 32-9-32-32-9 32-9z" fill="var(--accent)" stroke="var(--ink)" stroke-width="3"/>
      </svg>
      <span class="stick stick-c">4 бренд-сета</span>
      <span class="stick stick-a">100% громко</span>
      <span class="stick stick-b">без шаблонов ✦</span>
    </div>
  </div>
  <div class="scroll-hint" aria-hidden="true">скролл ↓</div>
</section>

<!-- MARQUEE -->
<div class="marquee" aria-hidden="true">
  <div class="mq-track">
    <div class="mq-group">
      <span>Брендинг <i>✦</i></span><span>UI/UX <i>✦</i></span><span>Веб-дизайн <i>✦</i></span><span>Моушн <i>✦</i></span><span>Леттеринг <i>✦</i></span><span>Упаковка <i>✦</i></span><span>Арт-дирекшн <i>✦</i></span><span>Стратегия <i>✦</i></span>
    </div>
    <div class="mq-group">
      <span>Брендинг <i>✦</i></span><span>UI/UX <i>✦</i></span><span>Веб-дизайн <i>✦</i></span><span>Моушн <i>✦</i></span><span>Леттеринг <i>✦</i></span><span>Упаковка <i>✦</i></span><span>Арт-дирекшн <i>✦</i></span><span>Стратегия <i>✦</i></span>
    </div>
  </div>
</div>

<!-- РАБОТЫ -->
<section id="works">
  <div class="wrap">
    <div class="sec-head" data-reveal>
      <p class="kicker">( 01 — работы )</p>
      <h2>Избранное <span class="outline">шоу</span></h2>
    </div>
    <div class="filters" data-reveal>
      <button type="button" class="filter-btn active" data-filter="all">Все</button>
      <button type="button" class="filter-btn" data-filter="brand">Брендинг</button>
      <button type="button" class="filter-btn" data-filter="web">Веб</button>
      <button type="button" class="filter-btn" data-filter="motion">Моушн</button>
    </div>
    <div class="works-grid" id="worksGrid">
      <a class="project-card" href="#contact" data-cat="brand" data-reveal>
        <div class="pc-media"><img data-img="p1img" src="https://image.qwenlm.ai/public_source/5bef37b5-3cb3-4b68-8ba3-3a44ce242656/1588e9dc7-f2ff-495b-906a-2cee52eb3d99.png" alt="Проект SODA RIOT — упаковка" loading="lazy"></div>
        <div class="pc-body">
          <div class="pc-top"><span class="pc-idx">01</span><span>2024</span></div>
          <h3 data-t="p1Title">Soda Riot</h3>
          <p class="pc-tags" data-t="p1Tags">брендинг · упаковка · FMCG</p>
          <span class="pc-arrow">→</span>
        </div>
      </a>
      <a class="project-card" href="#contact" data-cat="web" data-reveal>
        <div class="pc-media"><img data-img="p2img" src="https://image.qwenlm.ai/public_source/5bef37b5-3cb3-4b68-8ba3-3a44ce242656/1eea4f83f-5a27-428c-af6d-0ff3671402b4.png" alt="Проект STEP/99 — e-commerce" loading="lazy"></div>
        <div class="pc-body">
          <div class="pc-top"><span class="pc-idx">02</span><span>2024</span></div>
          <h3 data-t="p2Title">Step/99</h3>
          <p class="pc-tags" data-t="p2Tags">e-commerce · веб · 3D</p>
          <span class="pc-arrow">→</span>
        </div>
      </a>
      <a class="project-card" href="#contact" data-cat="web" data-reveal>
        <div class="pc-media"><img data-img="p3img" src="https://image.qwenlm.ai/public_source/5bef37b5-3cb3-4b68-8ba3-3a44ce242656/19725e747-735b-4e24-b219-c670c5f4b99b.png" alt="Проект KVANT OS — цифровой продукт" loading="lazy"></div>
        <div class="pc-body">
          <div class="pc-top"><span class="pc-idx">03</span><span>2023</span></div>
          <h3 data-t="p3Title">Kvant OS</h3>
          <p class="pc-tags" data-t="p3Tags">продукт · UI/UX · fintech</p>
          <span class="pc-arrow">→</span>
        </div>
      </a>
      <a class="project-card" href="#contact" data-cat="brand" data-reveal>
        <div class="pc-media"><img data-img="p4img" src="https://image.qwenlm.ai/public_source/5bef37b5-3cb3-4b68-8ba3-3a44ce242656/15361e7bc-e573-4294-af9f-9fee8ea40d2f.png" alt="Проект FORMA MUSEUM — айдентика" loading="lazy"></div>
        <div class="pc-body">
          <div class="pc-top"><span class="pc-idx">04</span><span>2023</span></div>
          <h3 data-t="p4Title">Forma Museum</h3>
          <p class="pc-tags" data-t="p4Tags">айдентика · навигация · принт</p>
          <span class="pc-arrow">→</span>
        </div>
      </a>
      <a class="project-card" href="#contact" data-cat="web" data-reveal>
        <div class="pc-media"><img data-img="p5img" src="https://image.qwenlm.ai/public_source/5bef37b5-3cb3-4b68-8ba3-3a44ce242656/1addbe6e8-a22d-4130-b1a6-a217f11cf26d.png" alt="Проект PULSE — мобильное приложение" loading="lazy"></div>
        <div class="pc-body">
          <div class="pc-top"><span class="pc-idx">05</span><span>2025</span></div>
          <h3 data-t="p5Title">Pulse App</h3>
          <p class="pc-tags" data-t="p5Tags">mobile · UI/UX · дизайн-система</p>
          <span class="pc-arrow">→</span>
        </div>
      </a>
      <a class="project-card" href="#contact" data-cat="motion" data-reveal>
        <div class="pc-media"><img data-img="p6img" src="https://image.qwenlm.ai/public_source/5bef37b5-3cb3-4b68-8ba3-3a44ce242656/101341c40-bf20-40ac-8ae4-ef29dccd833e.png" alt="Проект BASS CULT — моушн-кампания" loading="lazy"></div>
        <div class="pc-body">
          <div class="pc-top"><span class="pc-idx">06</span><span>2025</span></div>
          <h3 data-t="p6Title">Bass Cult</h3>
          <p class="pc-tags" data-t="p6Tags">моушн · реклама · 3D</p>
          <span class="pc-arrow">→</span>
        </div>
      </a>
    </div>
  </div>
</section>

<!-- СТУДИЯ -->
<section id="studio">
  <div class="wrap">
    <div class="sec-head" data-reveal>
      <p class="kicker">( 02 — студия )</p>
      <h2>Мы — <span class="outline">громкие</span></h2>
    </div>
    <div class="studio-grid">
      <div class="manifesto" data-reveal>
        <p data-t="studioText1">BST — бюро, которое не умеет шептать. Мы собираем айдентики, сайты и моушн, которые <strong>невозможно проскроллить</strong>: толстые обводки, жёсткие тени, типографика в лоб.</p>
        <p data-t="studioText2">Один и тот же каркас мы одеваем в четыре характера — от кислотного web3 до швейцарской строгости. Бренд-сет переключается одним кликом: палитра, тон и заголовок пересобираются на лету.</p>
        <p data-t="studioText3">Никаких стоковых улыбок и «чистых минималистичных» отговорок. Только характер, сетка и смелость.</p>
      </div>
      <div class="stats" data-reveal>
        <div class="stat"><span class="stat-num"><span data-count="8">0</span><em> лет</em></span><small data-t="stat1L">в деле и в шумe</small></div>
        <div class="stat"><span class="stat-num"><span data-count="120">0</span><em>+</em></span><small data-t="stat2L">проектов сдано</small></div>
        <div class="stat"><span class="stat-num"><span data-count="24">0</span><em>×</em></span><small data-t="stat3L">наград и шорт-листов</small></div>
        <div class="stat"><span class="stat-num"><span data-count="9000">0</span><em>+</em></span><small data-t="stat4L">чашек кофе выпито</small></div>
      </div>
    </div>
    <div class="quotes">
      <article class="quote-card" data-reveal>
        <p data-t="quote1">«BST переупаковали нас за месяц — заявки выросли в три раза. Это не дизайн, это чит-код.»</p>
        <footer data-t="quote1Author">— Аня К., CMO · KVANT OS</footer>
      </article>
      <article class="quote-card" data-reveal>
        <p data-t="quote2">«Единственная студия, которая спорила с нами и оказалась права.»</p>
        <footer data-t="quote2Author">— Игорь Л., фаундер · STEP/99</footer>
      </article>
      <article class="quote-card" data-reveal>
        <p data-t="quote3">«Громко, странно, идеально. Конкуренты до сих пор не понимают, как это сделано.»</p>
        <footer data-t="quote3Author">— Марго, бренд-лид · PULSE</footer>
      </article>
    </div>
  </div>
</section>

<!-- ПРОЦЕСС -->
<section id="process">
  <div class="wrap process-grid">
    <div class="proc-sticky" data-reveal>
      <p class="kicker">( 03 — процесс )</p>
      <h2 style="font-size:clamp(1.8rem,4vw,3rem);font-weight:900">Как мы<br>работаем</h2>
      <div class="step-counter" id="stepNum">01</div>
      <p class="proc-note">Четыре этапа, ноль воды. Скролльте — счётчик слева считает вместе с вами.</p>
    </div>
    <div>
      <div class="step" data-step="01">
        <h3><b>01</b> <span data-t="step1Title">Бриф и погружение</span></h3>
        <p data-t="step1Desc">Разбираем бизнес до винтиков: аудитория, конкуренты, страхи и амбиции. Формулируем платформу бренда и метрики успеха.</p>
      </div>
      <div class="step" data-step="02">
        <h3><b>02</b> <span data-t="step2Title">Концепция</span></h3>
        <p data-t="step2Desc">Три направления вместо «одного безопасного». Показываем честно, спорим громко, выбираем то, что работает.</p>
      </div>
      <div class="step" data-step="03">
        <h3><b>03</b> <span data-t="step3Title">Дизайн и моушн</span></h3>
        <p data-t="step3Desc">Собираем систему: логотип, цвет, типографика, веб, моушн-паки. Всё в одном токене — переключается за секунду.</p>
      </div>
      <div class="step" data-step="04">
        <h3><b>04</b> <span data-t="step4Title">Запуск и поддержка</span></h3>
        <p data-t="step4Desc">Гайдлайны, шаблоны, онбординг команды. Остаёмся рядом после релиза и следим, чтобы громкость не падала.</p>
      </div>
    </div>
  </div>
</section>

<!-- УСЛУГИ -->
<section id="services">
  <div class="wrap">
    <div class="sec-head" data-reveal>
      <p class="kicker">( 04 — услуги )</p>
      <h2>Что мы <span class="outline">делаем</span></h2>
    </div>
    <div class="acc-item" data-reveal>
      <button type="button" class="acc-head" aria-expanded="false">
        <span class="n">/01</span><h3>Брендинг и айдентика</h3><span class="acc-x">+</span>
      </button>
      <div class="acc-body"><div class="acc-inner">
        <p>Стратегия, нейминг, логотип, фирменный стиль и гайдлайны, которые реально используют, а не кладут в папку «забыть».</p>
        <div class="chips"><span>стратегия</span><span>логотип</span><span>гайдлайн</span><span>нейминг</span></div>
        <span class="price">от 450 000 ₽</span>
      </div></div>
    </div>
    <div class="acc-item" data-reveal>
      <button type="button" class="acc-head" aria-expanded="false">
        <span class="n">/02</span><h3>UI/UX и веб-дизайн</h3><span class="acc-x">+</span>
      </button>
      <div class="acc-body"><div class="acc-inner">
        <p>Сайты и продукты, которые продают: прототип, дизайн-система, адаптив, анимации и передача в разработку без слёз.</p>
        <div class="chips"><span>дизайн-система</span><span>прототип</span><span>адаптив</span><span>a11y</span></div>
        <span class="price">от 380 000 ₽</span>
      </div></div>
    </div>
    <div class="acc-item" data-reveal>
      <button type="button" class="acc-head" aria-expanded="false">
        <span class="n">/03</span><h3>Разработка</h3><span class="acc-x">+</span>
      </button>
      <div class="acc-body"><div class="acc-inner">
        <p>Фронтенд, который летает: 60 FPS, Lighthouse зелёный, анимации плавные, а деплой — скучный (в хорошем смысле).</p>
        <div class="chips"><span>frontend</span><span>webgl</span><span>cms</span><span>speed</span></div>
        <span class="price">от 500 000 ₽</span>
      </div></div>
    </div>
    <div class="acc-item" data-reveal>
      <button type="button" class="acc-head" aria-expanded="false">
        <span class="n">/04</span><h3>Моушн и 3D</h3><span class="acc-x">+</span>
      </button>
      <div class="acc-body"><div class="acc-inner">
        <p>Рекламные ролики, луки, стикер-паки и 3D-рендеры для соцсетей. Движение — это половина характера бренда.</p>
        <div class="chips"><span>2D/3D</span><span>рилсы</span><span>луки</span><span>заставки</span></div>
        <span class="price">от 250 000 ₽</span>
      </div></div>
    </div>
  </div>
</section>

<!-- ПАСПОРТ БРЕНДА -->
<section class="passport" id="passport">
  <div class="wrap" style="padding-top:clamp(56px,9vh,110px);padding-bottom:clamp(56px,9vh,110px)">
    <div class="sec-head" data-reveal>
      <p class="kicker">( 05 — айдентика )</p>
      <h2>Паспорт <span class="outline">бренда</span></h2>
    </div>
    <div class="pp-grid">
      <div data-reveal>
        <h3 class="pp-name">Сет <mark id="ppName">01 — ACID</mark></h3>
        <p class="pp-tag" id="ppTag">Кислотный неон для web3 и финтеха</p>
        <p class="pp-sub" id="ppSub">Громко. Ядовито. Дорого. Жирные контуры и ядовитый лайм — бренд, который первым занимает всё внимание в ленте.</p>
        <div class="pp-tone" id="ppTone"></div>
      </div>
      <div data-reveal>
        <div class="swatches">
          <div class="swatch"><div class="swatch-color" style="background:var(--bg)"></div><small>Фон<b id="hexBg">—</b></small></div>
          <div class="swatch"><div class="swatch-color" style="background:var(--surface)"></div><small>Панель<b id="hexSurface">—</b></small></div>
          <div class="swatch"><div class="swatch-color" style="background:var(--ink)"></div><small>Чернила<b id="hexInk">—</b></small></div>
          <div class="swatch"><div class="swatch-color" style="background:var(--accent)"></div><small>Акцент<b id="hexAccent">—</b></small></div>
          <div class="swatch"><div class="swatch-color" style="background:var(--accent2)"></div><small>Акцент 2<b id="hexAccent2">—</b></small></div>
        </div>
        <p class="pp-fonts">Дисплей: Unbounded 900 · Набор: IBM Plex Mono · Сетка: 12 кол. · Тень: 8px hard</p>
      </div>
    </div>
  </div>
</section>

<!-- MARQUEE 2 -->
<div class="marquee alt" aria-hidden="true">
  <div class="mq-track">
    <div class="mq-group">
      <span>Давайте сделаем громко <i>✦</i></span><span>BST® <i>✦</i></span><span>Давайте сделаем громко <i>✦</i></span><span>BST® <i>✦</i></span><span>Давайте сделаем громко <i>✦</i></span><span>BST® <i>✦</i></span>
    </div>
    <div class="mq-group">
      <span>Давайте сделаем громко <i>✦</i></span><span>BST® <i>✦</i></span><span>Давайте сделаем громко <i>✦</i></span><span>BST® <i>✦</i></span><span>Давайте сделаем громко <i>✦</i></span><span>BST® <i>✦</i></span>
    </div>
  </div>
</div>

<!-- ФУТЕР -->
<footer class="footer" id="contact">
  <div class="wrap">
    <p class="kicker" data-reveal>( 06 — контакт )</p>
    <h2 data-reveal>Давайте сделаем<br><span class="outline">громко</span> вместе</h2>
    <a class="btn btn-accent foot-mail" href="mailto:hello@bst.studio" data-reveal>hello@bst.studio ✦</a>
    <div class="foot-grid" data-reveal>
      <div>
        <h4>Меню</h4>
        <a href="#works">Работы</a><a href="#studio">Студия</a><a href="#process">Процесс</a><a href="#services">Услуги</a>
      </div>
      <div>
        <h4>Соцсети</h4>
        <a href="#top">Telegram ↗</a><a href="#top">Behance ↗</a><a href="#top">Dribbble ↗</a><a href="#top">VK ↗</a>
      </div>
      <div>
        <h4>Офисы</h4>
        <a href="#top">Москва</a><a href="#top">Белград</a><a href="#top">Тбилиси</a>
      </div>
    </div>
    <div class="foot-bottom">
      <span>© 2025 BST® Bureau — сделано громко</span>
      <span>активный сет: <span id="footBrand">01 / ACID</span></span>
      <a href="#top" style="text-decoration:none;font-weight:700">наверх ↑</a>
    </div>
  </div>
  <div class="foot-giant" aria-hidden="true">BST®</div>
</footer>

</main>
`

// ── Engine JS ─────────────────────────────────────────────────────────────────
function brutalPortfolioEngineJs(brand: string, mode: 'canvas' | 'preview'): string {
  return `
(function(){
  "use strict";
  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- ДАННЫЕ БРЕНД-СЕТОВ ---------- */
  var THEMES = {
    acid:{num:"01",name:"ACID",word:"КРИЧИТ",tag:"Кислотный неон для web3 и финтеха",
      sub:"Нео-брутализм без правил: жирные контуры, кислотные пятна и типографика, которую видно из космоса.",
      tone:["Громко","Ядовито","Дорого"]},
    pop:{num:"02",name:"POP",word:"ВЗРЫВАЕТ",tag:"Поп-арт энергия для FMCG и ивентов",
      sub:"Комиксы, стикеры и чистая эйфория. Дизайн, который хочется потрогать и немедленно купить.",
      tone:["Ярко","Сочно","Вкусно"]},
    noir:{num:"03",name:"NOIR",word:"УБЕЖДАЕТ",tag:"Швейцарская жёсткость для медиа и B2B",
      sub:"Чёрный, белый и один акцент. Максимальная читаемость, нулевая вода, железная сетка.",
      tone:["Чётко","Строго","Умно"]},
    candy:{num:"04",name:"CANDY",word:"ВЛЮБЛЯЕТ",tag:"Конфетный бунт для бьюти и лайфстайла",
      sub:"Пастель с характером: зефирные панели, толстые обводки и стикеры на каждом углу.",
      tone:["Нежно","Дерзко","Сладко"]}
  };

  var $ = function(s){return document.querySelector(s)};
  var $$ = function(s){return Array.prototype.slice.call(document.querySelectorAll(s))};

  /* ---------- SCRAMBLE ---------- */
  function scramble(el, text){
    if(!el) return;
    if(reduced){ el.textContent = text; return; }
    var chars = "█▓▒░#*+ЖШБЛФ";
    var frame = 0, total = 18;
    clearInterval(el._scr);
    el._scr = setInterval(function(){
      frame++;
      var out = "";
      for(var i=0;i<text.length;i++){
        out += (frame/total)*text.length > i ? text[i] : chars[Math.floor(Math.random()*chars.length)];
      }
      el.textContent = out;
      if(frame>=total){ clearInterval(el._scr); el.textContent = text; }
    },34);
  }

  /* ---------- HEX ИЗ CSS ---------- */
  function readHex(v){
    var val = getComputedStyle(document.documentElement).getPropertyValue(v).trim().toUpperCase();
    return val || "—";
  }
  function updatePassport(key){
    requestAnimationFrame(function(){
      var hBg=$("#hexBg"), hSurf=$("#hexSurface"), hInk=$("#hexInk"), hAcc=$("#hexAccent"), hAcc2=$("#hexAccent2");
      if(hBg) hBg.textContent = readHex("--bg");
      if(hSurf) hSurf.textContent = readHex("--surface");
      if(hInk) hInk.textContent = readHex("--ink");
      if(hAcc) hAcc.textContent = readHex("--accent");
      if(hAcc2) hAcc2.textContent = readHex("--accent2");
    });
    var t = THEMES[key];
    var ppName=$("#ppName"), ppTag=$("#ppTag"), ppSub=$("#ppSub"), ppTone=$("#ppTone");
    if(ppName) ppName.textContent = t.num + " — " + t.name;
    if(ppTag) ppTag.textContent = t.tag;
    if(ppSub){
      ppSub.textContent = t.sub;
      ppSub.classList.remove("swap"); void ppSub.offsetWidth; ppSub.classList.add("swap");
    }
    if(ppTone){
      ppTone.innerHTML = "";
      t.tone.forEach(function(w){
        var s = document.createElement("span"); s.textContent = w; ppTone.appendChild(s);
      });
    }
  }

  /* ---------- ПЕРЕКЛЮЧЕНИЕ СЕТА ---------- */
  function setBrand(key){
    if(document.documentElement.dataset.brand === key) return;
    document.documentElement.dataset.brand = key;
    $$(".sw-btn").forEach(function(b){ b.classList.toggle("active", b.dataset.set === key); });
    var t = THEMES[key];
    scramble($("#heroWord"), t.word);
    var hs = $("#heroSub");
    if(hs){
      hs.textContent = t.sub;
      hs.classList.remove("swap"); void hs.offsetWidth; hs.classList.add("swap");
    }
    var label = t.num + " / " + t.name;
    var hb=$("#heroBrand"), fb=$("#footBrand"), mb=$("#mmBrand");
    if(hb) hb.textContent = label;
    if(fb) fb.textContent = label;
    if(mb) mb.textContent = label;
    document.title = "BST® — сет " + t.name;
    updatePassport(key);
  }
  $$(".sw-btn").forEach(function(b){
    b.addEventListener("click", function(){ setBrand(b.dataset.set); });
  });

  /* ---------- МОБИЛЬНОЕ МЕНЮ ---------- */
  var burger = $("#burger"), mmenu = $("#mmenu");
  if(burger && mmenu){
    function toggleMenu(force){
      var open = force !== undefined ? force : !mmenu.classList.contains("open");
      mmenu.classList.toggle("open", open);
      burger.classList.toggle("open", open);
      burger.setAttribute("aria-expanded", String(open));
      mmenu.setAttribute("aria-hidden", String(!open));
      document.body.style.overflow = open ? "hidden" : "";
    }
    burger.addEventListener("click", function(){ toggleMenu(); });
    $$(".mmenu a").forEach(function(a){ a.addEventListener("click", function(){ toggleMenu(false); }); });
    document.addEventListener("keydown", function(e){ if(e.key === "Escape") toggleMenu(false); });
  }

  /* ---------- REVEAL ---------- */
  var io = new IntersectionObserver(function(es){
    es.forEach(function(e){ if(e.isIntersecting){ e.target.classList.add("in"); io.unobserve(e.target); } });
  },{threshold:.12});
  $$("[data-reveal]").forEach(function(el){ io.observe(el); });

  /* ---------- СЧЁТЧИКИ ---------- */
  var cio = new IntersectionObserver(function(es){
    es.forEach(function(e){
      if(!e.isIntersecting) return;
      var el = e.target, target = +el.dataset.count;
      cio.unobserve(el);
      if(reduced){ el.textContent = target; return; }
      var t0 = performance.now(), dur = 1400;
      (function tick(now){
        var p = Math.min((now - t0)/dur, 1);
        el.textContent = Math.round(target * (1 - Math.pow(1-p,3)));
        if(p < 1) requestAnimationFrame(tick);
      })(t0);
    });
  },{threshold:.6});
  $$("[data-count]").forEach(function(el){ cio.observe(el); });

  /* ---------- ШАГИ ПРОЦЕССА ---------- */
  var sio = new IntersectionObserver(function(es){
    es.forEach(function(e){
      if(e.isIntersecting){
        $$(".step").forEach(function(s){ s.classList.remove("active"); });
        e.target.classList.add("active");
        var sn=$("#stepNum"); if(sn) sn.textContent = e.target.dataset.step;
      }
    });
  },{threshold:.55});
  $$(".step").forEach(function(s){ sio.observe(s); });

  /* ---------- АККОРДЕОН ---------- */
  $$(".acc-item").forEach(function(item){
    var head = item.querySelector(".acc-head"), body = item.querySelector(".acc-body");
    if(!head || !body) return;
    head.addEventListener("click", function(){
      var open = item.classList.contains("open");
      $$(".acc-item.open").forEach(function(o){
        o.classList.remove("open");
        var ob=o.querySelector(".acc-body"); if(ob) ob.style.maxHeight = null;
        var oh=o.querySelector(".acc-head"); if(oh) oh.setAttribute("aria-expanded","false");
      });
      if(!open){
        item.classList.add("open");
        body.style.maxHeight = body.scrollHeight + "px";
        head.setAttribute("aria-expanded","true");
      }
    });
  });

  /* ---------- ФИЛЬТР РАБОТ ---------- */
  $$(".filter-btn").forEach(function(btn){
    btn.addEventListener("click", function(){
      $$(".filter-btn").forEach(function(b){ b.classList.remove("active"); });
      btn.classList.add("active");
      var f = btn.dataset.filter;
      $$(".project-card").forEach(function(card){
        var show = f === "all" || card.dataset.cat === f;
        card.classList.toggle("is-hidden", !show);
        if(show){ card.classList.remove("pop"); void card.offsetWidth; card.classList.add("pop"); }
      });
    });
  });

  /* ---------- СТАРТ ---------- */
  updatePassport("${brand}");
})();
${mode === 'canvas' ? `(function(){
  document.querySelectorAll('[data-reveal],.reveal,.in-view').forEach(function(el){el.classList.add('in','visible','in-view');});
  document.body.classList.add('loaded','js-loaded');
  document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(a)e.preventDefault();},true);
  document.querySelectorAll('form').forEach(function(f){f.addEventListener('submit',function(e){e.preventDefault();});});
})();` : ''}
`
}

// ── Public API ────────────────────────────────────────────────────────────────
export function getBrutalPortfolioPageHtml(data: BrutalPortfolioPageData, mode: 'canvas' | 'preview'): string {
  const brand = data.brand ?? 'acid'
  return `<!DOCTYPE html>
<html lang="ru" data-brand="${brand}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>BST® — Бюро громкого дизайна</title>
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/fonts/unbounded@latest/cyrillic-400-normal.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/fonts/unbounded@latest/cyrillic-700-normal.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/fonts/unbounded@latest/cyrillic-900-normal.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/fonts/unbounded@latest/latin-700-normal.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/fonts/unbounded@latest/latin-900-normal.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/fonts/ibm-plex-mono@latest/cyrillic-400-normal.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/fonts/ibm-plex-mono@latest/cyrillic-700-normal.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/fonts/ibm-plex-mono@latest/latin-400-normal.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/fontsource/fonts/ibm-plex-mono@latest/latin-700-normal.css">
<style>${BRUTAL_PORTFOLIO_CSS}</style>
</head>
<body>
${BRUTAL_PORTFOLIO_HTML_BODY}
<script>${brutalPortfolioEngineJs(brand, mode)}</script>
</body>
</html>`
}
